/**
 * The menus on the list's column headers: a filter for the column, opened from
 * the arrow beside its name, with "Clear filter" at the bottom — the way a
 * business application filters a column, and the reason the row of boxes above
 * the table is gone.
 */

import { useTranslation } from "react-i18next";
import { Button, Checkbox, ColumnMenu, Input } from "@hardauto/core-ui";

import { NAMESPACE } from "../../i18n";

/** A column whose filter is a piece of text: "contains this". */
export function TextFilterMenu({
  column,
  value,
  onChange,
}: {
  column: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <ColumnMenu
      label={t("consultation.columnMenu.filter", { column })}
      active={value.trim() !== ""}
    >
      <Input
        type="search"
        aria-label={t("consultation.fieldLabel", { field: column })}
        placeholder={t("consultation.fieldPlaceholder", { field: column })}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <Button variant="ghost" size="sm" disabled={value.trim() === ""} onClick={() => onChange("")}>
        {t("consultation.columnMenu.clear")}
      </Button>
    </ColumnMenu>
  );
}

/** The brand column: tick the brands to keep. */
export function BrandFilterMenu({
  column,
  options,
  selected,
  onChange,
}: {
  column: string;
  options: readonly string[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <ColumnMenu
      label={t("consultation.columnMenu.filter", { column })}
      active={selected.length > 0}
    >
      {options.map((brand) => (
        <Checkbox
          key={brand}
          label={brand}
          checked={selected.includes(brand)}
          onChange={(event) =>
            onChange(
              event.target.checked
                ? [...selected, brand]
                : selected.filter((item) => item !== brand),
            )
          }
        />
      ))}
      <Button
        variant="ghost"
        size="sm"
        disabled={selected.length === 0}
        onClick={() => onChange([])}
      >
        {t("consultation.columnMenu.clear")}
      </Button>
    </ColumnMenu>
  );
}

/** The quantity column: keep the articles with at least this much on hand. */
export function QuantityFilterMenu({
  column,
  value,
  onChange,
}: {
  column: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  return (
    <ColumnMenu
      label={t("consultation.columnMenu.filter", { column })}
      active={value.trim() !== ""}
    >
      <Input
        type="number"
        min={0}
        inputMode="decimal"
        aria-label={t("consultation.columnMenu.minQuantity")}
        placeholder={t("consultation.columnMenu.minQuantity")}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <Button variant="ghost" size="sm" disabled={value.trim() === ""} onClick={() => onChange("")}>
        {t("consultation.columnMenu.clear")}
      </Button>
    </ColumnMenu>
  );
}
